import type { Location } from 'history'
import { NavigationPrompt } from '@cbgms/base/navigation-prompt/NavigationPrompt'
import ConfirmNavigationOrderModal from './ConfirmNavigationOrderModal'
import { useFeature } from '@cbgms/base/feature-flags'
import { shouldShowConfirmation, shouldSkipConfirmation } from './util'
import { ActionBeforeLogout, BaseDialogVersion, BaseNavigationConfirmation } from './BaseNavigationConfirmation'
import ConfirmNavigationModal from '@cbgms/components/layout/ConfirmNavigationModal'
import { useSaveAndDeleteBeforeNavigate } from '@cbgms/base/navigation-prompt/navigation-save-and-delete-context'
import { useRef } from 'react'
import { BaseNavigationDialogContent } from './BaseNavigationDialogContent'

const ORDER_ID_RE = /^\/orders\/([^/]+)(?:\/|$)/

const getOrderId = (path?: string): string | undefined => {
  if (!path) return undefined
  const pathMatch = path.match(ORDER_ID_RE)
  if (!pathMatch) return undefined
  try {
    return decodeURIComponent(pathMatch[1])
  } catch {
    return pathMatch[1]
  }
}

const isOrderToDifferentOrder = (current: Location, next?: Location): boolean => {
  if (!next) return false
  const curId = getOrderId(current.pathname)
  const nextId = getOrderId(next.pathname)
  return !!(curId && nextId && curId !== nextId)
}

interface Props {
  baseDialogVersion?: `${BaseDialogVersion}`
  name?: string
  when?: (currentLocation: Location, nextLocation?: Location) => boolean
  validateBeforeNavigate?: () => Promise<Record<string, any> | void>
  saveBeforeNavigate?: () => Promise<void>
  handleShopChange?: (shopId: string) => Promise<void>
  handleLogout?: (orderAction: `${ActionBeforeLogout}`) => Promise<void>
  isPortal?: boolean
}

export function BaseNavigationGuards({
  baseDialogVersion,
  name,
  when,
  validateBeforeNavigate,
  saveBeforeNavigate,
  handleShopChange,
  handleLogout,
  isPortal = false
}: Props) {
  const hasB2BOrderPopup = useFeature('b2b-order-exit-popup')
  const { deleteBeforeNavigate } = useSaveAndDeleteBeforeNavigate()
  const pendingShopChangeRef = useRef(false)
  const pendingShopIdRef = useRef<string | null>(null)
  const pendingLogoutRef = useRef(false)

  const is404Route = (path?: string): boolean => !!path && /^\/404(?:$|\/)/.test(path)
  const isOrdersUUIDPath = (path?: string): boolean => !!path && /^\/orders\/[^/]+(?:$|\/)/.test(path)
  const isOrderToOtherRoute = (current: Location, next?: Location): boolean =>
    !!next && isOrdersUUIDPath(current.pathname) && !isOrdersUUIDPath(next.pathname)

  const handleShopChangeAndLogout = async (orderAction: `${ActionBeforeLogout}`) => {
    if (pendingShopChangeRef.current && handleShopChange) {
      try {
        const shopId = pendingShopIdRef.current
        if (shopId) await handleShopChange(shopId)
      } finally {
        pendingShopChangeRef.current = false
        pendingShopIdRef.current = null
      }
    }

    if (pendingLogoutRef.current && handleLogout) {
      try {
        await handleLogout(orderAction)
      } finally {
        pendingLogoutRef.current = false
      }
    }
  }

  const saveAndLogoutBeforeNavigate = async () => {
    if (validateBeforeNavigate && saveBeforeNavigate) {
      const errs = await validateBeforeNavigate()
      if (!(errs && Object.keys(errs).length)) {
        await saveBeforeNavigate()
      }
    }
    if (!validateBeforeNavigate && saveBeforeNavigate) {
      await saveBeforeNavigate()
    }
    await handleShopChangeAndLogout(ActionBeforeLogout.OrderUpdate)
  }

  const deleteAndLogoutBeforeNavigate = async () => {
    if (deleteBeforeNavigate) {
      await deleteBeforeNavigate()
    }
    await handleShopChangeAndLogout(ActionBeforeLogout.OrderDelete)
  }

  const orderToOtherRouteWhen = (current: Location, next?: Location) => {
    if (!hasB2BOrderPopup) return false
    if (!isPortal) return false
    if (!next) return false
    if (shouldSkipConfirmation(next)) return false
    if (is404Route(next.pathname)) return false
    pendingShopChangeRef.current = Boolean((next.state as { pendingShopChange?: boolean; shopID: string })?.pendingShopChange)
    pendingShopIdRef.current = (next.state as { pendingShopChange?: boolean; shopID: string })?.shopID
    pendingLogoutRef.current = Boolean((next.state as { pendingLogout?: boolean })?.pendingLogout)
    if ((isOrderToOtherRoute(current, next) || isOrderToDifferentOrder(current, next)) && shouldShowConfirmation(next)) {
      return true
    }
    const returnOrder =
      !!next && current.pathname !== next.pathname && (isOrderToOtherRoute(current, next) || isOrderToDifferentOrder(current, next))
    return returnOrder
  }

  const defaultRouteWhen: (current: Location, next?: Location) => boolean = (current, next) => {
    if (!next) return false
    if (shouldSkipConfirmation(next)) return false
    if (is404Route(next.pathname)) return false
    if (!when) return false
    // First check if there are unsaved changes using the when condition
    const hasUnsavedChanges = when(current, next)
    if (!hasUnsavedChanges) return false
    // Only if there are unsaved changes, consider the order route change
    if (orderToOtherRouteWhen(current, next)) {
      return true
    }
    return true
  }

  if (isPortal) {
    return (
      <>
        {when && (
          <NavigationPrompt
            name={name ?? 'base-navigation-confirmation'}
            component={
              !baseDialogVersion || baseDialogVersion === BaseDialogVersion.MuiUi ? BaseNavigationDialogContent : ConfirmNavigationModal
            }
            when={defaultRouteWhen}
          />
        )}
        <NavigationPrompt
          name='order-navigation-confirmation'
          component={ConfirmNavigationOrderModal}
          when={orderToOtherRouteWhen}
          saveBeforeNavigate={saveAndLogoutBeforeNavigate}
          deleteBeforeNavigate={deleteAndLogoutBeforeNavigate}
        />
      </>
    )
  } else {
    return when ? (
      <BaseNavigationConfirmation name={name ?? 'base-navigation-confirmation'} when={when} baseDialogVersion={baseDialogVersion} />
    ) : null
  }
}
